<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { BigNumber } from "ethers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import { useStoreApp } from "~/stores/storeApp";
import { useStorePack } from "~/stores/storePack";
import { Auction, AuctionStatus, Currency, MarketType } from "fungi-types";
import CardDataFooterSkeleton from "../card/CardDataFooterSkeleton.vue";
import CardDataFooterPrice from "../card/CardDataFooterPrice.vue";
import usePOLPrice from "~/stores/POLprice";

import NftSkillCard from "./NftSkill.vue";

import useConvert from "~/composables/useConvert";
import { PackStockList } from "~/api/auction/types";
import { useI18n } from "vue-i18n";

import { useStoreUserV2 } from "../../stores/storeUserV2";
const storeUser = useStoreUserV2();

const { t } = useI18n();

dayjs.extend(utc);
dayjs.extend(duration);

const props = withDefaults(
  defineProps<{
    item: PackStockList[number];
    showBuyButton?: boolean;
  }>(),
  {
    showBuyButton: true,
  }
);

const { maticToWei } = useConvert;
const polPrice = usePOLPrice();
const timeOver = ref(false);

const auctionStatus = ref<AuctionStatus>(AuctionStatus.IN_PROGRESS);

const _weiAmount = computed(() => {
  const wpol = polPrice.currencyToPol(props.item.amount, Currency.EUR);
  const wei = maticToWei(wpol);
  const amount = BigNumber.from(wei);
  return amount;
});

const handleTimeoutEvent = () => {
  timeOver.value = true;
};

function buyItem() {
  useStorePack().selectedItem = props.item;
  useStoreApp().setPackModalState(true);
}

const handleClickOnCard = () => buyItem();
const isSoldOut = computed(() => {
  return props.item.stock === 0;
});

watch(() => storeUser.createdAt);

const auction = computed(() => {
  let auction: Auction = {
    cuid: props.item.id,
    startedAmount: _weiAmount.value.toString(),
    endedAt: props.item.date,
    status: auctionStatus.value,
    marketType: MarketType.PRIMARY,
    lastBidder: null,
    lastBidAt: null,
    countOrders: 0,
    lastOrderId: null,
    extraTime: 0,
    lastBidAmount: _weiAmount.value.toString(),
    type: "pack",
  };
  return auction;
});
</script>

<template>
  <NftSkillCard class="w-fit hover:cursor-pointer" :nft="item" @click="handleClickOnCard">
    <template #footer-data>
      <CardDataFooterSkeleton>
        <div class="flex flex-col -gap-2">
          <div class="font-semibold tracking-wider">
            {{
              t("_shared.pack", {
                scarcity: t(`scarcities.${item.pack_type}`),
                gender: t(`genders.${item?.gender}`),
              }).toUpperCase()
            }}
          </div>
          <span class="text-neutral-400 text-xs">
            • {{ t("pack.cards-per-skill", { count: 4 }) }}
          </span>
          <span class="text-neutral-400 text-xs">
            • {{ t("pack.tokens-range", { from: item.tokens.from, to: item.tokens.to }) }}
          </span>
        </div>

        <CardDataFooterPrice
          v-if="auctionStatus"
          :auction="auction"
          :stock="item.stock"
          @time-out="handleTimeoutEvent"
        />
      </CardDataFooterSkeleton>
    </template>

    <template #cta v-if="showBuyButton">
      <ButtonPrimary :disabled="isSoldOut" @click="buyItem" class="w-full capitalize">
        {{ isSoldOut ? $t("_payment.sold-out") : $t("_payment.buy") }}
      </ButtonPrimary>
    </template>
  </NftSkillCard>
</template>

<style scoped lang="scss">
.winner {
  animation: winner 0.6s cubic-bezier(0.87, 0, 0.13, 1);
}

@keyframes winner {
  from {
    scale: 1.66;
    filter: drop-shadow(0 0 4em #ffffff);
  }
  to {
    scale: 1;
    filter: drop-shadow(0 0 0em #ffffff);
  }
}
</style>
